import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, X } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import type { ProductBrandProductBrandReadModel } from '@r/api/generated/client.schemas.ts';
import { useDebounce } from '@r/hooks/use-debounce.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Button } from '@ui/button.tsx';
import {
  apiAdminshopsShopIdbrandsIdPatch,
  apiShopsShopIdbrandsGetCollectionWithFilters,
  getAdminShopBrandsCatalogQueryKey,
  hasBrandNameConflict
} from './custom-shop-brands-api.ts';
import {
  buildCustomShopBrandFormSchema,
  type CustomShopBrandFormValues
} from './custom-shop-brand-form-schema.ts';

interface UpdateCustomShopBrandFormProps {
  shopId: number;
  brand: ProductBrandProductBrandReadModel;
  onCancel: () => void;
  onSuccess: () => void;
}

/** Met à jour une marque produit rattachée à une boutique custom. */
export function UpdateCustomShopBrandForm({
  shopId,
  brand,
  onCancel,
  onSuccess
}: UpdateCustomShopBrandFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSchema = useMemo(() => buildCustomShopBrandFormSchema(t), [t]);

  const form = useForm<CustomShopBrandFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: brand.name },
    mode: 'onChange'
  });

  const brandName = form.watch('name');
  const debouncedBrandName = useDebounce(brandName, 500);
  const trimmedDebouncedBrandName = debouncedBrandName.trim();
  const hasNameChanged =
    trimmedDebouncedBrandName.toLocaleLowerCase() !== brand.name.trim().toLocaleLowerCase();

  const existingBrandQuery = useQuery({
    queryKey: [
      'shop-brand-exists',
      shopId,
      brand.id,
      trimmedDebouncedBrandName.toLocaleLowerCase()
    ],
    queryFn: ({ signal }) =>
      apiShopsShopIdbrandsGetCollectionWithFilters(
        String(shopId),
        { name: trimmedDebouncedBrandName },
        signal
      ),
    enabled: trimmedDebouncedBrandName.length > 0 && hasNameChanged
  });

  const brandAlreadyExists = hasBrandNameConflict(
    existingBrandQuery.data ?? [],
    trimmedDebouncedBrandName,
    brand.id
  );

  /** Vérifie l'unicité du nom puis met à jour la marque si le libellé a changé. */
  const onSubmit = async (values: CustomShopBrandFormValues) => {
    const normalizedName = values.name.trim();
    if (!normalizedName) return;

    if (!form.formState.dirtyFields.name) {
      onSuccess();
      return;
    }

    setIsSubmitting(true);

    try {
      const existingBrands = await apiShopsShopIdbrandsGetCollectionWithFilters(String(shopId), {
        name: normalizedName
      });

      if (hasBrandNameConflict(existingBrands, normalizedName, brand.id)) {
        form.setError('name', {
          type: 'manual',
          message: t('admin.shops.brands.form.name.exists')
        });
        return;
      }

      await apiAdminshopsShopIdbrandsIdPatch(String(shopId), String(brand.id), {
        name: normalizedName
      });
      await queryClient.invalidateQueries({
        queryKey: getAdminShopBrandsCatalogQueryKey(shopId)
      });

      toast.success(t('admin.shops.brands.update.success'));
      onSuccess();
    } catch (error) {
      showOrvalErrorToast(error, t('admin.shops.brands.update.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.brands.form.name.label')}</FormLabel>
              <FormControl>
                <Input
                  maxLength={255}
                  error={!!fieldState.error || brandAlreadyExists}
                  {...field}
                />
              </FormControl>
              {existingBrandQuery.isFetching &&
                hasNameChanged &&
                trimmedDebouncedBrandName.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('admin.shops.brands.form.name.checking')}
                  </p>
                )}
              {brandAlreadyExists && (
                <p className="text-xs text-destructive">
                  {t('admin.shops.brands.form.name.exists')}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            <X className="size-4" />
            {t('label.cancel')}
          </Button>
          <Button
            type="submit"
            rounded="full"
            className="flex-1"
            loading={isSubmitting}
            disabled={brandAlreadyExists || existingBrandQuery.isFetching}
          >
            {t('form.save.modifications')}
            <Check className="size-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
